import React, { useContext } from "react";
import { Card, Heading, Separator } from "uikit/components";
import cx from "classnames";
import styles from "./RegionSummaryCard.module.styl";
import Chart from "react-apexcharts";
import { exact, number, string } from "prop-types";
import IconTooltip from "uikit/components/IconTooltip/IconTooltip";
import { percentageLevels } from "utils/indicator/indicatorSettings.js";
import { BuildReportFileTrigger } from "components";
import RegionSummaryApiClient from "api/RegionSummaryApiClient";
import { ReportFormat } from "api/Enums";
import { MediaContext } from "app/media/MediaContext";

export default function RegionSummaryCard({
    regionId,
    title,
    value,
    percentage,
    info,
    descriptionTitle,
    descriptionText,
    regionPositionModel,
    cardName,
}) {
    const options = {
        labels: [""],
        fill: {
            type: "solid",
            colors: [percentageLevels.indicatorColor(percentage).color],
        },
        plotOptions: {
            radialBar: {
                hollow: {
                    size: "75%",
                },
                track: {
                    background: "#C4C4C4",
                    strokeWidth: "35%",
                    margin: 2,
                },
                dataLabels: {
                    showOn: "always",
                    value: {
                        offsetY: -5,
                        color: "#082254",
                        fontSize: "24px",
                        show: true,
                        fontFamily: "Montserrat",
                        fontWeight: "600",
                    },
                },
            },
        },
    };
    const series = [percentage];

    const { isMobile } = useContext(MediaContext);

    return isMobile ? (
        <Card
            borderRadius={"giant"}
            padding={"none"}
            className={cx(styles.form, percentageLevels.indicatorColor(percentage).style)}
            name={cardName}
        >
            <div>
                <div className={styles.heading}>
                    <Heading size={"h2"}>{title}</Heading>
                    <div className={styles.icon}>
                        <IconTooltip trigger={"hover"}>
                            <div className={styles.infoPanel}>{info}</div>
                        </IconTooltip>
                    </div>
                </div>
                <Separator margin={"none"} color="grey" />
                <div className={styles.chart}>
                    <Chart options={options} series={series} type="radialBar" width="185" />
                    <div
                        className={cx(
                            styles.percentageText,
                            percentageLevels.indicatorColor(percentage).textStyle,
                        )}
                    >
                        {percentage >= percentageLevels.normal
                            ? percentage >= percentageLevels.good
                                ? "высокий уровень цифровизации"
                                : "средний уровень цифровизации"
                            : "низкий уровень цифровизации"}
                    </div>
                </div>
                <Separator margin={"none"} color="grey" />
                <div className={styles.indicators}>
                    <div className={styles.firstColumn}>
                        <div className={styles.indicator}>
                            <div className={styles.numerator}>
                                {regionPositionModel.regionPosition}
                            </div>
                            <div className={styles.denominator}>
                                /{regionPositionModel.regionsCount}
                            </div>
                        </div>
                        <div className={styles.regionRatingTitle}>
                            {regionPositionModel.regionRatingText}
                        </div>
                    </div>
                    <div>
                        <div className={styles.indicator}>
                            <div className={styles.numerator}>{value}</div>
                        </div>
                        <div className={styles.descriptionTitle}>{descriptionTitle}</div>
                    </div>
                </div>
                <div className={styles.linkContainer}>
                    <div className={styles.descriptionText}>{descriptionText}</div>
                    <BuildReportFileTrigger
                        startBuildingReportFunc={() =>
                            RegionSummaryApiClient.buildRegionSummary(regionId, ReportFormat.Excel)
                        }
                        formatIcon="Excel"
                        triggerText="Скачать подробный отчет"
                        downloadText="Скачать Excel-отчет"
                    />
                </div>
            </div>
        </Card>
    ) : (
        <Card
            borderRadius={"giant"}
            padding={"none"}
            className={cx(styles.form, percentageLevels.indicatorColor(percentage).style)}
        >
            <div className={styles.content} data-test-name="region_summary">
                <div>
                    <div className={styles.heading}>
                        <Heading size={"h2"}>{title}</Heading>
                        <div className={styles.icon}>
                            <IconTooltip trigger={"hover"}>
                                <div className={styles.infoPanel}>{info}</div>
                            </IconTooltip>
                        </div>
                    </div>
                    <div className={styles.indicators}>
                        <div className={styles.regionRating}>
                            <div className={styles.indicator}>
                                <div className={styles.numerator}>
                                    {regionPositionModel.regionPosition}
                                </div>
                                <div className={styles.denominator}>
                                    /{regionPositionModel.regionsCount}
                                </div>
                            </div>
                            <div className={styles.regionRatingTitle}>
                                {regionPositionModel.regionRatingText}
                            </div>
                        </div>
                        <div>
                            <div className={styles.indicator}>
                                <div className={styles.numerator}>{value}</div>
                            </div>
                            <div className={styles.descriptionTitle}>{descriptionTitle}</div>
                        </div>
                    </div>
                    <div className={styles.linkContainer}>
                        <div className={styles.descriptionText}>{descriptionText}</div>
                        <BuildReportFileTrigger
                            startBuildingReportFunc={() =>
                                RegionSummaryApiClient.buildRegionSummary(
                                    regionId,
                                    ReportFormat.Excel,
                                )
                            }
                            formatIcon="Excel"
                            triggerText="Скачать подробный отчет"
                            downloadText="Скачать Excel-отчет"
                        />
                    </div>
                </div>
                <div className={styles.chart}>
                    <Chart options={options} series={series} type="radialBar" width="185" />
                    <div
                        className={cx(
                            styles.percentageText,
                            percentageLevels.indicatorColor(percentage).textStyle,
                        )}
                    >
                        {percentage >= percentageLevels.normal
                            ? percentage >= percentageLevels.good
                                ? "высокий уровень цифровизации"
                                : "средний уровень цифровизации"
                            : "низкий уровень цифровизации"}
                    </div>
                </div>
            </div>
        </Card>
    );
}

RegionSummaryCard.propTypes = {
    title: string.isRequired,
    percentage: number.isRequired,
    value: number.isRequired,
    info: string.isRequired,
    descriptionTitle: string.isRequired,
    descriptionText: string.isRequired,
    regionId: number.isRequired,
    regionPositionModel: exact({
        regionPosition: number.isRequired,
        regionsCount: number.isRequired,
        regionRatingText: string.isRequired,
    }).isRequired,
    cardName: string,
};
